import * as React from "react";
import useLocalStorage from "./useLocalStorage";
import { clearUploadQueue } from "../uploadQueue";

const authContext = React.createContext({
  username: null,
  setUsername: (_) => {},
});

function useAuth() {
  const [sessionToken, setStoredSessionToken] = useLocalStorage(
    "sessionToken",
    null,
  );
  // Every session change goes through here so queued uploads never start with
  // a token that's no longer current.
  const setSessionToken = React.useCallback(
    (token) => {
      clearUploadQueue();
      setStoredSessionToken(token);
    },
    [setStoredSessionToken],
  );
  // A token from localStorage might be expired. Until we've checked it against
  // the server we don't know — so start in the "validating" state if we loaded
  // with a token, and let RequireAuth hold off rendering until we're done.
  const [isValidating, setIsValidating] = React.useState(sessionToken !== null);

  React.useEffect(() => {
    if (sessionToken === null) {
      setIsValidating(false);
      return;
    }

    let cancelled = false;
    fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/auth/session`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionToken}`,
      },
    })
      .then((response) => {
        if (!cancelled && response.status === 401) {
          setSessionToken(null);
        }
      })
      .catch((error) => {
        // Network error — keep the token rather than bouncing the user to
        // login just because the API was briefly unreachable.
        console.log("Error validating session:", error);
      })
      .finally(() => {
        if (!cancelled) setIsValidating(false);
      });

    return () => {
      cancelled = true;
    };
    // Validate only the token we loaded on mount; a fresh login token is
    // already known-good.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = React.useCallback(
    (username, password) =>
      new Promise((resolve, reject) => {
        // TODO Move API calls elsewhere
        fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/auth/session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username, password }),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Unexpected status code ${response.status}`);
            }

            return response.json();
          })
          .then((data) => {
            setSessionToken(data.data.sessionToken);
            resolve();
          })
          .catch((error) => {
            console.log("Error authenticating:", error);
            reject(error);
          });
      }),
    [setSessionToken],
  );

  const logout = React.useCallback(
    () =>
      new Promise((res) => {
        setSessionToken(null);
        res();
      }),
    [setSessionToken],
  );

  // Consumers list logout in effect deps (e.g. Gallery's fetchPage), so an
  // unstable value would re-run those effects and refetch on any auth render.
  return React.useMemo(
    () => ({ sessionToken, isValidating, login, logout }),
    [sessionToken, isValidating, login, logout],
  );
}

export function AuthProvider({ children }) {
  return (
    <authContext.Provider value={useAuth()}>{children}</authContext.Provider>
  );
}

export default function AuthConsumer() {
  return React.useContext(authContext);
}
